import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Client } from '@classes/client';
import { License } from '@classes/license';
import { Pagination } from '@classes/pagination/pagination';
import { PaginationRequest } from '@classes/pagination/pagination-request';
import { environment } from '@environments/environment';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LicensesService {
  private readonly BASE_URL: string = environment.apiUrl + 'licenses';

  private _licenses: License[];
  public licensesSubject: Subject<License[]> = new Subject<License[]>();

  private _pagination: Pagination;
  public paginationSubject: BehaviorSubject<Pagination> = new BehaviorSubject<Pagination>(null);

  private _lastRequest: PaginationRequest = null;

  constructor(
    private _httpClient: HttpClient
  ) { }

  public fetchLastRequest() {
    this.fetch(this._lastRequest);
  }

  public fetch(request: PaginationRequest): void {
    this._lastRequest = request;
    let httpParams = new HttpParams();

    httpParams = this._appendPaginationParams(httpParams, request);
    httpParams = this._appendSortParams(httpParams, request);
    httpParams = this._appendFilterParams(httpParams, request);

    this._httpClient.get(this.BASE_URL, { params: httpParams, observe: 'response' })
      .subscribe((response: HttpResponse<License[]>) => {
        this._licenses = response.body;
        this._pagination = JSON.parse(response.headers.get('X-Pagination'));

        this._updateLicensesSubject();
        this._updatePaginationSubject();
      });
  }

  private _appendPaginationParams(httpParams: HttpParams, { pageSize, page }: PaginationRequest): HttpParams {
    if (pageSize && page) {
      httpParams = httpParams
        .append("pageSize", pageSize.toString())
        .append("page", page.toString());
    }

    return httpParams;
  }

  private _appendSortParams(httpParams: HttpParams, { sort }: PaginationRequest): HttpParams {
    if (sort) {
      httpParams = httpParams.append("sorts", sort);
    }

    return httpParams;
  }

  private _appendFilterParams(httpParams: HttpParams, { query }: PaginationRequest): HttpParams {
    if (query) {
      const filtersQuery = [];

      if (query.clientName) {
        filtersQuery.push(`clientName@=${query.clientName}`);
      }

      const { startDate, endDate } = query.range;

      if (startDate && endDate) {
        filtersQuery.push(`startDate>=${moment(new Date(startDate)).startOf('day').utc().format("YYYY-MM-DD HH:mm")}`);
        filtersQuery.push(`endDate<=${moment(new Date(endDate)).endOf('day').utc().format("YYYY-MM-DD HH:mm")}`);
      }

      if (filtersQuery.length > 0) {
        httpParams = httpParams.append("filters", filtersQuery.join(','));
      }
    }

    return httpParams;
  }

  public fetchClientLicenses$({ id }: Client): Observable<License[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append("filters", `clientId==${id}`);

    return this._httpClient.get<License[]>(this.BASE_URL, { params: httpParams });
  }

  public post(license: License): void {
    this._httpClient
      .post(this.BASE_URL, license)
      .subscribe((response: License) => {
        this.fetchLastRequest();
      });
  }

  public put(license: License): void {
    this._httpClient
      .put(this.BASE_URL, license)
      .subscribe((response: License) => {
        this.fetchLastRequest();
      });
  }

  public delete$({ id }: License): Observable<License> {
    return this._httpClient
      .delete(`${this.BASE_URL}/${id}`)
      .pipe(
        map((response: License) => {
          this.fetchLastRequest();
          return response;
        })
      );
  }

  private _updateLicensesSubject(): void {
    this.licensesSubject.next([...this._licenses]);
  }

  private _updatePaginationSubject(): void {
    this.paginationSubject.next(this._pagination);
  }
}